import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINKS = [
    { href: "#readings", label: "The Readings", testid: "nav-link-readings", gold: true },
    { href: "#mythology", label: "The Mythology", testid: "nav-link-mythology" },
    { href: "#mara", label: "Mara", testid: "nav-link-mara" },
    { href: "#recognition", label: "The Recognition", testid: "nav-link-recognition" },
    { href: "#oracle", label: "Mirror Oracle", testid: "nav-link-oracle" },
    { href: "#truth", label: "The Truth", testid: "nav-link-truth" },
    { href: "#unique", label: "The Custom Working", testid: "nav-link-unique" },
    { href: "#whispers", label: "Whispers", testid: "nav-link-whispers" },
    { href: "#collection", label: "The Ritual Collection", testid: "nav-link-collection" },
];

export const Header = ({ onSpeak }) => {
    const [scrolled, setScrolled] = useState(false);
    const [freeNow, setFreeNow] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const load = () => axios.get(`${API}/promo`).then((r) => setFreeNow(!!r.data?.active)).catch(() => {});
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, []);

    return (
        <>
            <header
                data-testid="nav-header"
                className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-500 ${
                    scrolled || menuOpen
                        ? "border-purple-500/25 bg-[#06010f]/85 shadow-[0_4px_40px_rgba(123,44,191,0.25)] backdrop-blur-xl"
                        : "border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
                    <a href="#hero" data-testid="nav-logo" className="group flex items-center gap-3">
                        <span className="relative block h-11 w-11 overflow-hidden rounded-full ring-1 ring-[#c77dff]/50 transition-shadow duration-300 group-hover:shadow-[0_0_20px_rgba(199,125,255,0.6)]">
                            <img src="/assets/sisters-mark.png" alt="Dichotomy Sisters emblem" className="h-full w-full object-cover" />
                        </span>
                        <span className="font-cinzel text-sm font-semibold tracking-[0.3em] text-[#f8f7ff] sm:text-base">
                            DICHOTOMY <span className="text-[#c77dff]">SISTERS</span>
                        </span>
                    </a>

                    <nav className="hidden items-center gap-7 lg:flex">
                        {LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={l.testid}
                                className={`font-cinzel text-[11px] tracking-[0.2em] transition-colors duration-300 hover:text-glow-pearl ${
                                    l.gold && freeNow
                                        ? "rounded-full border border-[#ffd700]/40 bg-[#ffd700]/10 px-4 py-1.5 text-[#ffd700] hover:text-[#ffd700]"
                                        : "text-[#e0aaff]/70 hover:text-[#f8f7ff]"
                                }`}
                            >
                                {l.gold && freeNow ? "READINGS — FREE TODAY" : l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2.5">
                        <button type="button" onClick={onSpeak} data-testid="nav-cta-button" className="btn-ghost-glow flex items-center gap-2">
                            <MessageCircle className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">SPEAK TO MARA</span>
                            <span className="sm:hidden">MARA</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setMenuOpen((o) => !o)}
                            data-testid="mobile-menu-button"
                            aria-label={menuOpen ? "Close menu" : "Open menu"}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#c77dff]/40 text-[#e0aaff] transition-colors duration-300 hover:text-[#f8f7ff] lg:hidden"
                        >
                            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.nav
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.3 }}
                        data-testid="mobile-menu"
                        className="fixed inset-x-0 top-[68px] z-[60] border-b border-purple-500/25 bg-[#06010f]/95 backdrop-blur-2xl lg:hidden"
                    >
                        <div className="flex flex-col px-6 py-4">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setMenuOpen(false)}
                                    data-testid={`mobile-${l.testid}`}
                                    className={`border-b border-purple-500/10 py-3.5 font-cinzel text-xs tracking-[0.25em] transition-colors duration-300 ${
                                        l.gold && freeNow ? "text-[#ffd700]" : "text-[#e0aaff]/80 hover:text-[#f8f7ff]"
                                    }`}
                                >
                                    {l.gold && freeNow ? "READINGS — FREE TODAY" : l.label}
                                </a>
                            ))}
                            <a
                                href="#booking"
                                onClick={() => setMenuOpen(false)}
                                data-testid="mobile-nav-link-booking"
                                className="py-3.5 font-cinzel text-xs tracking-[0.25em] text-[#e0aaff]/80 hover:text-[#f8f7ff]"
                            >
                                INITIATE A RITUAL
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </>
    );
};
